const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

// Coordinates follow the main image's object-fit: cover crop, not the uncropped file.
export function calculateLens({ width, height, imageWidth, imageHeight, x, y, size = 180, zoom = 2, positionX = .5, positionY = .5 }) {
  if (![width, height, imageWidth, imageHeight].every(value => Number.isFinite(value) && value > 0)) return null
  size = Math.min(size, width, height)
  const scale = Math.max(width / imageWidth, height / imageHeight)
  const drawnWidth = imageWidth * scale
  const drawnHeight = imageHeight * scale
  const imageLeft = (width - drawnWidth) * positionX
  const imageTop = (height - drawnHeight) * positionY
  return {
    size,
    x: clamp(x - size / 2, 0, width - size),
    y: clamp(y - size / 2, 0, height - size),
    imageWidth: drawnWidth * zoom,
    imageHeight: drawnHeight * zoom,
    imageX: clamp(size / 2 - (x - imageLeft) * zoom, size - drawnWidth * zoom, 0),
    imageY: clamp(size / 2 - (y - imageTop) * zoom, size - drawnHeight * zoom, 0),
  }
}

export function attachGalleryTools(viewport) {
  if (!viewport || viewport.querySelector('[data-gallery-lens]')) return
  const gallery = viewport.closest('.product-gallery')
  if (!gallery) return
  const mouseHover = window.matchMedia('(hover: hover) and (pointer: fine)')
  const lens = document.createElement('div')
  lens.className = 'product-gallery__lens'
  lens.dataset.galleryLens = ''
  lens.hidden = true
  lens.setAttribute('aria-hidden', 'true')
  const enlarged = document.createElement('img')
  enlarged.alt = ''
  enlarged.draggable = false
  lens.append(enlarged)
  viewport.append(lens)

  let frame = 0
  let point = null
  const reset = () => {
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    point = null
    lens.hidden = true
    viewport.classList.remove('is-magnifying')
  }
  const getImage = () => viewport.querySelector('[data-gallery-primary]') || viewport.querySelector('[data-gallery-slide]:not([hidden]) img')
  const render = () => {
    frame = 0
    const image = getImage()
    if (!point || !mouseHover.matches || !image) return reset()
    if (!image.complete || !image.naturalWidth) {
      lens.hidden = true
      viewport.classList.remove('is-magnifying')
      return
    }
    const box = viewport.getBoundingClientRect()
    const x = (point.x - box.left) * viewport.clientWidth / box.width
    const y = (point.y - box.top) * viewport.clientHeight / box.height
    if (x < 0 || y < 0 || x > viewport.clientWidth || y > viewport.clientHeight) return reset()
    const position = getComputedStyle(image).objectPosition.split(' ').map(value => value.endsWith('%') ? clamp(parseFloat(value) / 100, 0, 1) : .5)
    const geometry = calculateLens({
      width: viewport.clientWidth, height: viewport.clientHeight,
      imageWidth: image.naturalWidth, imageHeight: image.naturalHeight,
      x, y, size: Math.min(200, viewport.clientWidth * .4),
      positionX: position[0], positionY: position[1] ?? .5,
    })
    if (!geometry) return reset()
    const source = image.currentSrc || image.src
    if (enlarged.src !== source) enlarged.src = source
    lens.style.width = `${geometry.size}px`
    lens.style.height = `${geometry.size}px`
    lens.style.transform = `translate3d(${geometry.x}px, ${geometry.y}px, 0)`
    enlarged.style.width = `${geometry.imageWidth}px`
    enlarged.style.height = `${geometry.imageHeight}px`
    enlarged.style.transform = `translate3d(${geometry.imageX}px, ${geometry.imageY}px, 0)`
    lens.hidden = false
    viewport.classList.add('is-magnifying')
  }
  const move = event => {
    if (!mouseHover.matches || event.pointerType !== 'mouse' || event.target.closest?.('button, a')) return reset()
    point = { x: event.clientX, y: event.clientY }
    if (!frame) frame = requestAnimationFrame(render)
  }
  viewport.addEventListener('pointermove', move)
  viewport.addEventListener('pointerleave', reset)
  viewport.addEventListener('pointercancel', reset)
  viewport.addEventListener('load', event => {
    if (event.target === getImage() && point && !frame) frame = requestAnimationFrame(render)
  }, true)
  viewport.addEventListener('error', event => {
    if (event.target === getImage()) reset()
  }, true)
  gallery.addEventListener('gallery:change', reset)
  window.addEventListener('scroll', reset, { passive: true, capture: true })
  window.addEventListener('resize', reset, { passive: true })
  mouseHover.addEventListener('change', reset)

  if (gallery.querySelectorAll('[data-gallery-slide]').length < 2) return

  for (const step of [-1, 1]) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'product-gallery__image-arrow'
    button.dataset.galleryImageStep = String(step)
    button.setAttribute('aria-label', step < 0 ? 'Previous image' : 'Next image')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('aria-hidden', 'true')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', step < 0 ? 'm14 5-7 7 7 7' : 'm10 5 7 7-7 7')
    svg.append(path)
    button.append(svg)
    button.addEventListener('click', () => {
      reset()
      viewport.dispatchEvent(new CustomEvent('gallery:step', { bubbles: true, detail: { step } }))
    })
    viewport.append(button)
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-gallery-viewport]').forEach(attachGalleryTools)
}
